import { Web3Address } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { EMilliSeconds } from 'modules/common/constants/const';
import { PACKAGE_BUNDLE_ID } from 'domains/account/const';
import { RootState } from 'store';
import { fetchBalance } from 'modules/billing/actions/fetchBalance';
import { fetchBundlePaymentPlans } from 'domains/account/actions/bundles/fetchBundlePaymentPlans';
import { fetchReferralPrograms } from 'modules/strapi/actions/fetchReferralPrograms';
import { getDateFromUnixSeconds } from 'modules/common/utils/getDateFromUnixSeconds';
import { getReferralProgram } from 'modules/referralProgram/utils/getReferralProgram';
import { selectReferrer } from 'modules/referralProgram/actions/fetchReferrer';

import { CREDITS_TO_REQUESTS_RATE, DEFAULT_PAYG_BALANCE } from '../constants';
import {
  ChargingModel,
  PAYGChargingModel,
  PAYGChargingModelBalance,
} from '../types';
import { fetchMyBundlesStatus } from '../actions/fetchMyBundlesStatus';
import { getDealChargingModel } from '../utils/getDealChargingModel';
import { getDefaultFreemiumChargingModel } from '../utils/getDefaultFreemiumChargingModel';
import { getFreemiumChargingModel } from '../utils/getFreemiumChargingModel';
import { getPackageChargingModel } from '../utils/getPackageChargingModel';
import { getPromoChargingModel } from '../utils/getPromoChargingModel';
import { sortChargingModels } from '../utils/sortChargingModels';

const selectBalanceData = createSelector(
  (state: RootState, group: Web3Address | undefined) =>
    fetchBalance.select({ group })(state),
  ({ data = DEFAULT_PAYG_BALANCE }) => data,
);

export const selectPAYGBalance = createSelector(
  selectBalanceData,
  balanceData => {
    const credits = Number(balanceData.balance);
    const isNegativeBalance = credits <= 0;

    const requests = credits / CREDITS_TO_REQUESTS_RATE;

    const paygBalance: PAYGChargingModelBalance = {
      ankr: Number(balanceData.balance_ankr),
      balanceLevel: balanceData.balance_level,
      credits,
      requests: isNegativeBalance ? 0 : requests,
      usd: Number(balanceData.balance_usd),
      voucher: Number(balanceData.balance_voucher),
    };

    return paygBalance;
  },
);

const selectFreemiumBundlePaymentPlans = createSelector(
  fetchBundlePaymentPlans.select(),
  plans =>
    plans.data?.filter(plan => plan.bundle.type === 'FREE_ALLOWANCE_FREEMIUM'),
);

const selectPaidBundlePaymentPlans = createSelector(
  fetchBundlePaymentPlans.select(),
  plans => plans.data?.filter(plan => plan.bundle.type === 'PAID'),
);

const selectPromoBundlePaymentPlans = createSelector(
  fetchBundlePaymentPlans.select(),
  plans => plans.data?.filter(plan => plan.bundle.type === 'PROMO'),
);

const selectMyBundlesStatusState = createSelector(
  (state: RootState, group: Web3Address | undefined) =>
    fetchMyBundlesStatus.select({ group })(state),
  state => state,
);

const selectPromoBundle = createSelector(
  selectReferrer,
  selectMyBundlesStatusState,
  fetchReferralPrograms.select(),
  (referrer, { data: myBundles }, { data: referralPrograms }) => {
    const referralCode = referrer?.referral_code;

    const referralProgram = getReferralProgram({
      referralCode,
      referralPrograms,
    });

    if (referralProgram) {
      const { bundleID } = referralProgram;

      const promoBundle = myBundles?.find(
        bundle => bundle.bundleId === bundleID,
      );

      return promoBundle;
    }

    return undefined;
  },
);

const selectHasPromoBundle = createSelector(selectPromoBundle, promoBundle => {
  const expiresMs = (promoBundle?.expires ?? 0) * EMilliSeconds.Second;

  const isPromoBundleExpired = new Date() > new Date(expiresMs);

  return !isPromoBundleExpired;
});

const selectDealChargingModels = createSelector(
  selectMyBundlesStatusState,
  selectPaidBundlePaymentPlans,
  (bundlesStatus, plans) => {
    const dealBundlesStatus = bundlesStatus.data?.filter(
      bundle =>
        bundle.bundleId !== PACKAGE_BUNDLE_ID &&
        plans?.some(plan => plan.bundle.bundle_id === bundle.bundleId),
    );

    const filteredByExpiration = dealBundlesStatus?.filter(
      deal => new Date() < new Date(getDateFromUnixSeconds(deal.expires)),
    );

    return filteredByExpiration?.map(bundleStatus =>
      getDealChargingModel({
        bundleStatus,
        plans,
      }),
    );
  },
);

const selectFreemiumChargingModel = createSelector(
  selectMyBundlesStatusState,
  selectFreemiumBundlePaymentPlans,
  (_state: RootState, _group: Web3Address | undefined, isFreemium: boolean) =>
    isFreemium,
  (bundlesStatus, plans, isFreemium) => {
    if (isFreemium) {
      const freemiumBundlesStatus = bundlesStatus.data?.filter(status =>
        plans?.some(plan => plan.bundle.bundle_id === status.bundleId),
      );

      const firstFreemiumBundleStatus = freemiumBundlesStatus?.[0];

      if (firstFreemiumBundleStatus) {
        return getFreemiumChargingModel({
          bundleStatus: firstFreemiumBundleStatus,
          plans,
        });
      }

      // freemium allowance bundle is not activated yet
      if (plans) {
        return getDefaultFreemiumChargingModel({ plans });
      }
    }

    return undefined;
  },
);

export const selectPAYGChargingModel = createSelector(
  selectPAYGBalance,
  (_state: RootState, _group: Web3Address | undefined, isFreemium: boolean) =>
    isFreemium,
  (balance, isFreemium) => {
    if (isFreemium) {
      return undefined;
    }

    const chargingModelPayg: PAYGChargingModel = {
      type: 'payg',
      balance,
    };

    return chargingModelPayg;
  },
);

const selectPackageChargingModels = createSelector(
  selectMyBundlesStatusState,
  selectPaidBundlePaymentPlans,
  (myByndlesStatusState, plans) => {
    const packageChargingModels = myByndlesStatusState.data?.filter(
      bundle => bundle.bundleId === PACKAGE_BUNDLE_ID,
    );

    const filteredByExpiration = packageChargingModels?.filter(
      bundle => new Date() < new Date(getDateFromUnixSeconds(bundle.expires)),
    );

    return filteredByExpiration
      ?.sort((a, b) => a.expires - b.expires)
      .map(bundleStatus =>
        getPackageChargingModel({
          bundleStatus,
          plans,
        }),
      );
  },
);

export const selectPromoChargingModel = createSelector(
  selectMyBundlesStatusState,
  selectPromoBundlePaymentPlans,
  selectHasPromoBundle,
  (bundlesStatus, plans, hasPromoBundle) => {
    if (!hasPromoBundle) {
      return undefined;
    }

    const promoBundlesStatus = bundlesStatus.data?.filter(bundle =>
      plans?.some(plan => plan.bundle.bundle_id === bundle.bundleId),
    );

    const firstPromoBundleStatus = promoBundlesStatus?.[0];

    if (firstPromoBundleStatus) {
      return getPromoChargingModel({
        bundleStatus: firstPromoBundleStatus,
        plans,
      });
    }

    return undefined;
  },
);

export const selectChargingModels = createSelector(
  selectDealChargingModels,
  selectFreemiumChargingModel,
  selectPackageChargingModels,
  selectPAYGChargingModel,
  selectPromoChargingModel,
  // eslint-disable-next-line max-params
  (deal, freemium, packageModels, payg, promo) =>
    sortChargingModels({
      dealChargingModels: deal,
      freemiumChargingModel: freemium,
      packageChargingModels: packageModels,
      paygChargingModel: payg,
      promoChargingModel: promo,
    }),
);

export const selectCurrentChargingModel = createSelector(
  selectChargingModels,
  (models): ChargingModel | undefined => models[0],
);

export const selectCurrentNonPromoChargingModel = createSelector(
  selectDealChargingModels,
  selectFreemiumChargingModel,
  selectPackageChargingModels,
  selectPAYGChargingModel,
  // eslint-disable-next-line max-params
  (deal, freemium, packageModels, payg): ChargingModel | undefined => {
    const models = sortChargingModels({
      dealChargingModels: deal,
      freemiumChargingModel: freemium,
      packageChargingModels: packageModels,
      paygChargingModel: payg,
      promoChargingModel: undefined,
    });

    return models[0];
  },
);
